import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useThemeSettings } from '../../contexts/ThemeContext';
import LocationDialog from '../../components/LocationDialog';
import DeviceDialog from '../../components/DeviceDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import ButtonTestDialog from '../../components/EnOcean/ButtonTestDialog';

// Hooks
import { useLocations } from './hooks/useLocations';
import { useLocationFilters } from './hooks/useLocationFilters';
import { useLocationSelection } from './hooks/useLocationSelection';
import { useLocationDialogs } from './hooks/useLocationDialogs';
import { useExpectedDevices } from './hooks/useExpectedDevices';
import { useLocationBreadcrumb } from './hooks/useLocationBreadcrumb';

// Components
import LocationHeader from './components/LocationHeader';
import LocationBreadcrumbBar from './components/LocationBreadcrumbBar';

// Views
import TreeView from './components/views/TreeView';
import NetworkView from './components/views/NetworkView';
import FlatListView from './components/views/FlatListView';
import CardGridView from './components/views/CardGridView';
import BreadcrumbDrillView from './components/views/BreadcrumbDrillView';

// Utils
import { VIEW_MODES } from './utils/locationConstants';
import { getLocationId, findLocationById } from './utils/locationTransformers';

/**
 * Location Management - Thin orchestrator
 * Composes hooks + views following the TicketsManagement pattern
 */
const LocationManagementEnhanced = () => {
  const { t } = useThemeSettings();

  // --- Hooks ---
  const {
    flatLocations,
    hierarchicalLocations,
    loading,
    error,
    total,
    refresh
  } = useLocations();

  const {
    viewMode,
    page,
    rowsPerPage,
    typeFilter,
    searchFilter,
    searchInputValue,
    sortBy,
    sortOrder,
    hasActiveFilters,
    handleViewModeChange,
    handleSearchInputChange,
    handleTypeFilterChange,
    handleSortChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleResetFilters,
    applyFilters,
    paginate
  } = useLocationFilters();

  const {
    selectedLocation,
    setSelectedLocation,
    selectLocation,
    selectedTab,
    setSelectedTab,
    devices,
    setDevices,
    loadingDevices,
    locationPersons,
    loadingPersons,
    enoceanButtons,
    loadingButtons,
    buttonError,
    setButtonError,
    handleDeleteButton,
    detailPanelRef,
    refreshSelection
  } = useLocationSelection({ hierarchicalLocations });

  const dialogs = useLocationDialogs({
    selectedLocation,
    setSelectedLocation,
    selectLocation,
    refresh,
    t
  });

  const expectedDevices = useExpectedDevices({
    selectedLocation,
    setSelectedLocation,
    devices,
    setLocations: null // hierarchy is managed by useLocations
  });

  const breadcrumb = useLocationBreadcrumb({
    flatLocations,
    hierarchicalLocations
  });

  // --- Computed data ---
  const filteredLocations = useMemo(() =>
    applyFilters(flatLocations),
    [flatLocations, applyFilters]
  );

  const paginatedLocations = useMemo(() =>
    paginate(filteredLocations),
    [filteredLocations, paginate]
  );

  // Breadcrumb path for the selected location
  const selectedBreadcrumbPath = useMemo(() => {
    if (!selectedLocation) return [];
    return breadcrumb.getBreadcrumbPath(getLocationId(selectedLocation));
  }, [selectedLocation, breadcrumb]);

  // Sibling navigation for the selected location
  const siblingNavigation = useMemo(() => {
    return breadcrumb.getSiblingNavigation(selectedLocation);
  }, [selectedLocation, breadcrumb]);

  // Navigate to a location from breadcrumb
  const handleBreadcrumbNavigate = (locationId) => {
    const location = findLocationById(hierarchicalLocations, locationId);
    if (location) selectLocation(location);
  };

  // Navigate to sibling
  const handleNavigateSibling = (location) => {
    selectLocation(location);
  };

  // --- Detail panel props (shared across all views) ---
  const detailPanelProps = {
    selectedTab,
    setSelectedTab,
    devices,
    loadingDevices,
    locationPersons,
    loadingPersons,
    enoceanButtons,
    loadingButtons,
    buttonError,
    setButtonError,
    hierarchicalLocations,
    // Expected devices
    ...expectedDevices,
    // Navigation
    siblingNavigation,
    onNavigateSibling: handleNavigateSibling,
    // Actions
    onSelectLocation: selectLocation,
    setSelectedLocation,
    setLocations: null,
    onAddLocation: dialogs.handleAddLocation,
    onEditLocation: dialogs.handleEditLocation,
    onDeleteLocation: dialogs.handleDeleteLocation,
    onAddDevice: dialogs.handleAddDevice,
    onEditDevice: dialogs.handleEditDevice,
    onDeleteDevice: dialogs.handleDeleteDevice,
    onDeleteButton: handleDeleteButton,
    onOpenButtonTest: () => dialogs.setButtonTestDialogOpen(true),
    onRefresh: refresh,
    t
  };

  // Refresh trigger as a number for tree view
  const refreshTrigger = useMemo(() => Date.now(), [hierarchicalLocations]);

  // --- Render ---
  return (
    <Box
      className="page-transition-enter"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {/* Header with search, view toggle, actions */}
      <LocationHeader
        viewMode={viewMode}
        searchInputValue={searchInputValue}
        typeFilter={typeFilter}
        hasActiveFilters={hasActiveFilters}
        onViewModeChange={handleViewModeChange}
        onSearchChange={handleSearchInputChange}
        onResetFilters={handleResetFilters}
        onAddLocation={dialogs.handleAddLocation}
        t={t}
      />

      {/* Breadcrumb bar - visible when a location is selected */}
      {selectedLocation && (
        <LocationBreadcrumbBar
          breadcrumbPath={selectedBreadcrumbPath}
          onNavigate={handleBreadcrumbNavigate}
        />
      )}

      {/* View Content */}
      {viewMode === VIEW_MODES.TREE && (
        <TreeView
          selectedLocation={selectedLocation}
          refreshTrigger={refreshTrigger}
          detailPanelRef={detailPanelRef}
          onLocationSelect={selectLocation}
          onLocationEdit={dialogs.handleEditLocation}
          onLocationDelete={dialogs.handleDeleteLocation}
          onLocationAdd={dialogs.handleAddLocation}
          detailPanelProps={detailPanelProps}
        />
      )}

      {viewMode === VIEW_MODES.NETWORK && (
        <NetworkView
          locations={hierarchicalLocations}
          loading={loading}
          error={error}
          selectedLocation={selectedLocation}
          onLocationSelect={selectLocation}
          onLocationEdit={dialogs.handleEditLocation}
          onLocationDelete={dialogs.handleDeleteLocation}
          onLocationAdd={dialogs.handleAddLocation}
          onRetry={refresh}
          t={t}
        />
      )}

      {viewMode === VIEW_MODES.FLAT_LIST && (
        <FlatListView
          flatLocations={flatLocations}
          filteredLocations={filteredLocations}
          paginatedLocations={paginatedLocations}
          totalCount={filteredLocations.length}
          loading={loading}
          error={error}
          selectedLocation={selectedLocation}
          sortBy={sortBy}
          sortOrder={sortOrder}
          page={page}
          rowsPerPage={rowsPerPage}
          onSortChange={handleSortChange}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onLocationSelect={selectLocation}
          onRetry={refresh}
          detailPanelRef={detailPanelRef}
          detailPanelProps={detailPanelProps}
          t={t}
        />
      )}

      {viewMode === VIEW_MODES.CARD_GRID && (
        <CardGridView
          flatLocations={flatLocations}
          filteredLocations={filteredLocations}
          paginatedLocations={paginatedLocations}
          totalCount={filteredLocations.length}
          loading={loading}
          error={error}
          selectedLocation={selectedLocation}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onLocationSelect={selectLocation}
          onRetry={refresh}
          detailPanelRef={detailPanelRef}
          detailPanelProps={detailPanelProps}
          t={t}
        />
      )}

      {viewMode === VIEW_MODES.BREADCRUMB_DRILL && (
        <BreadcrumbDrillView
          childrenAtLevel={breadcrumb.childrenAtLevel}
          drillBreadcrumbPath={breadcrumb.drillBreadcrumbPath}
          loading={loading}
          error={error}
          selectedLocation={selectedLocation}
          onDrillInto={breadcrumb.drillInto}
          onDrillUp={breadcrumb.drillUp}
          onDrillToLevel={breadcrumb.drillToLevel}
          onLocationSelect={selectLocation}
          onRetry={refresh}
          detailPanelRef={detailPanelRef}
          detailPanelProps={detailPanelProps}
          t={t}
        />
      )}

      {/* Dialogs */}
      <LocationDialog
        open={dialogs.dialogOpen}
        mode={dialogs.dialogMode}
        location={dialogs.dialogMode === 'edit' ? selectedLocation : null}
        parentLocation={dialogs.parentLocation}
        onClose={() => dialogs.setDialogOpen(false)}
        onSave={dialogs.handleSave}
      />

      <ConfirmDialog
        open={dialogs.confirmDialogOpen}
        title={t ? t('LocationManagement.delete_location') : 'Delete Location'}
        content={`Are you sure you want to delete "${dialogs.locationToDelete?.name || dialogs.locationToDelete?.friendlyName}"? This action cannot be undone.`}
        confirmText={t ? t('DeviceTable.delete') : 'Delete'}
        confirmColor="error"
        onConfirm={dialogs.confirmDelete}
        onCancel={() => {
          dialogs.setConfirmDialogOpen(false);
          dialogs.setLocationToDelete(null);
        }}
      />

      <DeviceDialog
        open={dialogs.deviceDialogOpen}
        mode={dialogs.deviceDialogMode}
        device={dialogs.deviceToEdit}
        onClose={() => dialogs.setDeviceDialogOpen(false)}
        onSave={dialogs.handleDeviceSave}
      />

      <ConfirmDialog
        open={dialogs.deleteDeviceConfirmOpen}
        title={t ? t('DeviceTable.delete_device') : 'Delete Device'}
        content={`Are you sure you want to delete "${dialogs.deviceToDelete?.friendlyName || 'this device'}"? This action cannot be undone.`}
        confirmText={t ? t('DeviceTable.delete') : 'Delete'}
        confirmColor="error"
        onConfirm={dialogs.handleConfirmDeviceDelete}
        onCancel={() => dialogs.setDeleteDeviceConfirmOpen(false)}
      />

      <ButtonTestDialog
        open={dialogs.buttonTestDialogOpen}
        onClose={() => dialogs.setButtonTestDialogOpen(false)}
        locationId={getLocationId(selectedLocation)}
        locationName={selectedLocation?.friendlyName || selectedLocation?.name}
        onTestComplete={() => {
          if (selectedLocation) selectLocation(selectedLocation);
        }}
      />
    </Box>
  );
};

export default LocationManagementEnhanced;
